export function postedYear(date: string | null | undefined): string {
  const year = String(date || '').replace(/T.*/, '').slice(0, 4);
  return /^\d{4}$/.test(year) ? year : '';
}

export function chartYearKeys(dates: Array<string | null | undefined>): string[] {
  return Array.from(new Set(dates.map(postedYear).filter(Boolean))).sort();
}

const YEAR_LEGEND_BASE = '#4B5563';
const YEAR_SHADE_WHITE_MIX = 0.58;

function parseHex(hex: string): [number, number, number] {
  const raw = hex.replace('#', '');
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((part) => `${part}${part}`)
          .join('')
      : raw;
  const value = Number.parseInt(full, 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function toHex(r: number, g: number, b: number): string {
  return `#${[r, g, b]
    .map((channel) => Math.round(channel).toString(16).padStart(2, '0'))
    .join('')}`.toUpperCase();
}

export function mixHex(from: string, toward: string, amount: number): string {
  const t = Math.min(1, Math.max(0, amount));
  const [fr, fg, fb] = parseHex(from);
  const [tr, tg, tb] = parseHex(toward);
  return toHex(fr + (tr - fr) * t, fg + (tg - fg) * t, fb + (tb - fb) * t);
}

export function amountShadeFill(baseColor: string, amount: number, amounts: number[]): string {
  const ranked = Array.from(new Set(amounts.filter((value) => value > 0))).sort((left, right) => left - right);
  if (ranked.length <= 1) return baseColor;
  const index = ranked.indexOf(amount);
  if (index < 0) return mixHex(baseColor, '#FFFFFF', YEAR_SHADE_WHITE_MIX);
  const darkestWeight = index / (ranked.length - 1);
  return mixHex(baseColor, '#FFFFFF', YEAR_SHADE_WHITE_MIX * (1 - darkestWeight));
}

export function rowYearShadeFill(
  baseColor: string,
  year: string,
  byYear: Record<string, number> | undefined,
  years: string[]
): string {
  const amounts = years.map((key) => Number(byYear?.[key] || 0));
  return amountShadeFill(baseColor, Number(byYear?.[year] || 0), amounts);
}

export function chartYearTotals(
  rows: Array<{ byYear?: Record<string, number> }>,
  years: string[]
): Record<string, number> {
  const totals: Record<string, number> = {};
  years.forEach((year) => {
    totals[year] = rows.reduce((sum, row) => sum + Number(row.byYear?.[year] || 0), 0);
  });
  return totals;
}

export function yearLegendFill(year: string, yearTotals: Record<string, number>): string {
  return amountShadeFill(YEAR_LEGEND_BASE, Number(yearTotals[year] || 0), Object.values(yearTotals));
}

export function yearLegendText(year: string, yearTotals: Record<string, number>): string {
  const [r, g, b] = parseHex(yearLegendFill(year, yearTotals));
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? '#111827' : '#FFFFFF';
}

export function addYearAmount(
  byYear: Record<string, number>,
  year: string,
  amount: number
): Record<string, number> {
  if (!year || !(amount > 0)) return byYear;
  byYear[year] = (byYear[year] || 0) + amount;
  return byYear;
}

export function mergeYearAmounts(
  left: Record<string, number> | undefined,
  right: Record<string, number> | undefined
): Record<string, number> {
  const next = { ...(left || {}) };
  Object.entries(right || {}).forEach(([year, amount]) => {
    next[year] = (next[year] || 0) + Number(amount || 0);
  });
  return next;
}

export function flattenYearStack<T extends { byYear?: Record<string, number> }>(
  row: T,
  years: string[]
): T & Record<string, number> {
  const extra: Record<string, number> = {};
  years.forEach((year) => {
    extra[year] = Number(row.byYear?.[year] || 0);
  });
  return { ...row, ...extra };
}

export function chartAxisEuroTick(value: number | string): string {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '€0';
  if (amount === 0) return '€0';
  if (Math.abs(amount) >= 10_000 && amount % 1000 === 0) {
    return `€${(amount / 1000).toLocaleString('en-IE')}k`;
  }
  const digits = Number.isInteger(amount) || Math.abs(amount) >= 100 ? 0 : 2;
  return `€${amount.toLocaleString('en-IE', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
}

function niceAxisStep(span: number, targetTicks = 5): number {
  const range = Math.max(Math.abs(span), 1);
  const raw = range / Math.max(targetTicks, 1);
  const exp = Math.floor(Math.log10(raw));
  const mag = 10 ** exp;
  const frac = raw / mag;
  const niceFrac = frac <= 1.5 ? 1 : frac <= 3 ? 2 : frac <= 7 ? 5 : 10;
  return niceFrac * mag;
}

function nextNiceStep(step: number): number {
  const exp = Math.floor(Math.log10(step));
  const mag = 10 ** exp;
  const frac = step / mag;
  const nextFrac = frac < 2 ? 2 : frac < 5 ? 5 : 10;
  return nextFrac * mag;
}

function ticksForStep(min: number, max: number, step: number): number[] {
  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.max(niceMin + step, Math.ceil(max / step) * step);
  const steps = Math.round((niceMax - niceMin) / step);
  return Array.from({ length: steps + 1 }, (_, index) => niceMin + index * step);
}

/** Round domain and ticks so Cartesian gridlines land on 0 / 50k / 100k-style values. */
export function householdXAxisFromValues(values: number[]): {
  domain: [number, number];
  ticks: number[];
} {
  const nums = values.map(Number).filter((value) => Number.isFinite(value));
  const dataMin = nums.length ? Math.min(0, ...nums) : 0;
  const dataMax = nums.length ? Math.max(0, ...nums) : 0;
  const span = Math.max(dataMax - dataMin, 1);
  let step = niceAxisStep(span, 5);
  let ticks = ticksForStep(dataMin, dataMax, step);
  while (ticks.length > 5) {
    step = nextNiceStep(step);
    ticks = ticksForStep(dataMin, dataMax, step);
  }
  return { domain: [ticks[0], ticks[ticks.length - 1]], ticks };
}

/** Full-row hover band behind a vertical bar; pointer-events none so the chart click still fires. */
export const HOUSEHOLD_CHART_HOVER_CURSOR = {
  fill: 'rgba(15, 23, 42, 0.08)',
  pointerEvents: 'none' as const,
};

/** Invisible full-plot band behind each vertical bar so empty space in the hover row is clickable. */
export const HOUSEHOLD_CHART_BAR_BACKGROUND = {
  fill: 'rgba(15, 23, 42, 0)',
  cursor: 'pointer' as const,
};

export function householdChartClickName(
  state:
    | {
        activeLabel?: unknown;
        activePayload?: Array<{ payload?: { name?: unknown } }>;
        activeTooltipIndex?: number | string | null;
        activeIndex?: number | string | null;
      }
    | null
    | undefined,
  rows: Array<{ name: string }>
): string {
  if (!state) return '';
  const fromPayload = String(state.activePayload?.[0]?.payload?.name ?? '').trim();
  if (fromPayload) return fromPayload;
  const fromLabel = String(state.activeLabel ?? '').trim();
  if (fromLabel) return fromLabel;
  const raw = state.activeTooltipIndex ?? state.activeIndex;
  const index =
    typeof raw === 'number' && Number.isFinite(raw)
      ? raw
      : typeof raw === 'string' && raw.trim() !== ''
        ? Number(raw)
        : undefined;
  if (typeof index === 'number' && Number.isFinite(index) && rows[index]?.name) {
    return String(rows[index].name);
  }
  return '';
}
